import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Calendar,
  MapPin,
  UserCog,
  ChevronDown,
  Search,
  X,
  XCircle,
} from "lucide-react";
import { apiClient } from "../../services/apiClient";
import { addNotification } from "../../store/slices/uiSlice";
import { useAppDispatch } from "../../store/hooks";
import { webSocketService } from "../../services/websocketService";
import { logError } from "../../utils/errorLogger";

interface Guest {
  id: number;
  guest: string;
  guest_name: string;
  response_status: string;
  attended: 'pending' | 'attended' | 'not_attended';
  attendance_confirmed_by: string | null;
  attendance_confirmed_at: string | null;
  companion_name: string;
  companion_count: number | null;
  seat?: string;
}

interface Event {
  id: string;
  title: string;
  event_date: string;
  event_time: string;
  location: string;
}

// Attendance Dropdown Component
interface AttendanceDropdownProps {
  guestId: number;
  currentStatus: 'pending' | 'attended' | 'not_attended';
  onStatusChange: (guestId: number, attended: 'pending' | 'attended' | 'not_attended') => void;
  isUpdating: boolean;
}

const AttendanceDropdown: React.FC<AttendanceDropdownProps> = ({
  guestId,
  currentStatus,
  onStatusChange,
  isUpdating,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position
  const calculatePosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 100; // Approximate height for 2 options
    const dropdownWidth = rect.width;

    let top = rect.bottom + 4;
    let left = rect.left;

    // Check if dropdown fits below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      // Show above
      top = rect.top - dropdownHeight - 4;
    }

    // Ensure dropdown doesn't go off screen horizontally
    if (left + dropdownWidth > viewportWidth - 8) {
      left = viewportWidth - dropdownWidth - 8;
    }
    if (left < 8) {
      left = 8;
    }

    // Ensure dropdown doesn't go off screen vertically
    if (top < 8) {
      top = 8;
    }
    if (top + dropdownHeight > viewportHeight - 8) {
      top = viewportHeight - dropdownHeight - 8;
    }

    setDropdownPosition({ top, left, width: dropdownWidth });
  };

  // Update position when opening
  useEffect(() => {
    if (isOpen) {
      calculatePosition();

      // Recalculate on scroll and resize
      const handleUpdate = () => {
        if (isOpen) {
          calculatePosition();
        }
      };

      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);

      return () => {
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setDropdownPosition(null);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  const options: Array<{
    value: 'pending' | 'attended' | 'not_attended';
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  }> = [
    {
      value: 'attended',
      label: "Attended",
      icon: CheckCircle,
      color: "text-green-400",
    },
    {
      value: 'pending',
      label: "Pending",
      icon: Clock,
      color: "text-yellow-400",
    },
    {
      value: 'not_attended',
      label: "Not Attended",
      icon: XCircle,
      color: "text-red-400",
    },
  ];

  const currentOption = options.find((opt) => opt.value === currentStatus);

  const handleSelect = (attended: 'pending' | 'attended' | 'not_attended') => {
    onStatusChange(guestId, attended);
    setIsOpen(false);
    setDropdownPosition(null);
  };

  return (
    <>
      <div className="relative attendance-dropdown">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          disabled={isUpdating}
          className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm transition-all duration-300 border shadow-sm ${
            currentStatus === 'attended'
              ? "bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30"
              : currentStatus === 'pending'
              ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/30"
              : "bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30"
          } ${
            isUpdating
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:shadow-md"
          }`}
        >
          {isUpdating ? (
            <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              {currentOption && (
                <currentOption.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              )}
              <span className="whitespace-nowrap">{currentOption?.label}</span>
              <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
            </>
          )}
        </button>
      </div>

      {/* Dropdown Portal */}
      {isOpen &&
        !isUpdating &&
        dropdownPosition &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg shadow-lg z-[10001]"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {options.map((option) => (
              <button
                key={option.value.toString()}
                onClick={() => handleSelect(option.value)}
                className={`w-full flex items-center space-x-2 px-4 py-2 text-left hover:bg-white/10 transition-colors ${
                  option.value === currentStatus ? "bg-white/5" : ""
                }`}
              >
                <option.icon className={`h-4 w-4 ${option.color}`} />
                <span className={option.color}>{option.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

const UsherEventGuestsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [guests, setGuests] = useState<Guest[]>([]);
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);
  const [usherSocket] = useState<WebSocket | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(21);

  const fetchEventInfo = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch just event info (first call without search to get event details)
      const response = await apiClient.get(`/ushers/events/${eventId}/guests/`);

      if (response.data.success) {
        setEvent(response.data.event);
        // Don't set guests initially - only show on search
        setGuests([]);
      }
    } catch (error: any) {
      logError("Error fetching event info", error);
      dispatch(
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to load event information. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [eventId, dispatch]);

  useEffect(() => {
    if (eventId) {
      fetchEventInfo();

      // Subscribe to real-time attendance updates using the main WebSocket service
      const unsubscribe = webSocketService.on("guest_response", (data: any) => {
        if (data.data && data.data.type === "attendance_update") {
          setGuests((prevGuests) => {
            return prevGuests.map((guest) => {
              const matches = guest.id === parseInt(data.data.guest_event_id);
              return matches
                ? {
                    ...guest,
                    attended: data.data.attended,
                    attendance_confirmed_at: data.data.attendance_confirmed_at,
                    attendance_confirmed_by: data.data.attendance_confirmed_by,
                  }
                : guest;
            });
          });

          // UI will be updated directly - no toast notification needed
        }
      });

      // Subscribe to WebSocket errors
      const unsubscribeError = webSocketService.on(
        "websocket_error",
        (error: any) => {
          logError("Usher WebSocket error", error);
          dispatch(
            addNotification({
              type: "error",
              title: "Connection Error",
              message: `WebSocket error: ${error.message}`,
            })
          );
        }
      );

      // Subscribe to the specific event using the main WebSocket service
      // Ensure WebSocket is connected before subscribing
      if (!webSocketService.connectionStatus) {
        webSocketService.connect().catch((error) => {
          // Error is already logged in connect
        });
      }

      webSocketService.subscribeToEvent(eventId);

      return () => {
        unsubscribe();
        unsubscribeError();
        if (usherSocket) {
          usherSocket.close();
        }
      };
    }
  }, [eventId, dispatch, fetchEventInfo, usherSocket]);

  const handleSearch = async () => {
    if (!eventId || !searchQuery.trim()) {
      setGuests([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
      return;
    }

    try {
      setSearching(true);
      const response = await apiClient.get(`/ushers/events/${eventId}/guests/`, {
        params: {
          search: searchQuery.trim(),
          page: currentPage,
          page_size: pageSize
        }
      });

      if (response.data.success) {
        setGuests(response.data.guests || []);
        setTotalCount(response.data.count || response.data.guests?.length || 0);
        setTotalPages(Math.ceil((response.data.count || response.data.guests?.length || 0) / pageSize));
        
        if (response.data.guests && response.data.guests.length === 0 && currentPage === 1) {
          dispatch(
            addNotification({
              type: "info",
              title: "No Results",
              message: "No guests found matching your search.",
            })
          );
        }
      }
    } catch (error: any) {
      logError("Error searching guests", error);
      dispatch(
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to search guests. Please try again.",
        })
      );
      setGuests([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setGuests([]);
    setTotalCount(0);
    setTotalPages(1);
    setCurrentPage(1);
  };

  // Debounced search effect
  useEffect(() => {
    if (!eventId) return;

    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch();
      } else {
        setGuests([]);
        setTotalCount(0);
        setTotalPages(1);
        setCurrentPage(1);
      }
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, eventId, currentPage]);
  
  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleAttendanceToggle = async (
    guestEventId: number,
    attended: 'pending' | 'attended' | 'not_attended'
  ) => {
    try {
      setUpdating(String(guestEventId));
      const response = await apiClient.patch(
        `/ushers/attendance/${guestEventId}/`,
        {
          attended: attended,
        }
      );

      if (response.data.success) {
        // Update local state
        setGuests((prevGuests) =>
          prevGuests.map((guest) =>
            guest.id === guestEventId
              ? {
                  ...guest,
                  attended,
                  attendance_confirmed_at: new Date().toISOString(),
                }
              : guest
          )
        );

        // UI will be updated directly - no toast notification needed
      }
    } catch (error: any) {
      logError("Error updating attendance", error);
      dispatch(
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to update attendance. Please try again.",
        })
      );
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getResponseStatusColor = (status: string) => {
    switch (status) {
      case "accepted":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "rejected":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "pending":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "ignored":
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
          <p className="text-sm sm:text-base text-gray-400">Loading event...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => navigate("/usher/dashboard")}
                className="flex items-center space-x-1.5 sm:space-x-2 text-gray-300 hover:text-white transition-colors text-xs sm:text-sm"
              >
                <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </button>

              <div className="h-4 sm:h-6 w-px bg-white/20"></div>

              <div className="flex items-center space-x-2 sm:space-x-3">
                <UserCog className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
                <span className="text-sm sm:text-lg font-medium">
                  Event Guests
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Event Info */}
        {event && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">
              {event.title}
            </h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
              <div className="flex items-center space-x-2 text-gray-300">
                <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{formatDate(event.event_date)}</span>
              </div>

              <div className="flex items-center space-x-2 text-gray-300">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{formatTime(event.event_time)}</span>
              </div>

              <div className="flex items-center space-x-2 text-gray-300 sm:col-span-2 md:col-span-1">
                <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{event.location}</span>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search guests by name..."
                  className="w-full pl-8 sm:pl-10 pr-8 sm:pr-10 py-2 sm:py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm sm:text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                )}
              </div>
              {searching && (
                <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0"></div>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-400 mt-2 sm:mt-3 ml-1">
              Type a guest's name to search
            </p>
          </div>
        </div>

        {/* Search Results */}
        {searchQuery.trim() && (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h2 className="text-xl sm:text-2xl font-bold text-white">
                Search Results
              </h2>
              {totalCount > 0 && (
                <span className="text-xs sm:text-sm text-gray-400">
                  Found {totalCount}{" "}
                  {totalCount === 1 ? "guest" : "guests"}
                  {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                </span>
              )}
            </div>

            {searching ? (
              <div className="text-center py-8 sm:py-12">
                <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
                <p className="text-sm sm:text-base text-gray-400">
                  Searching...
                </p>
              </div>
            ) : guests.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <Search className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-300 mb-2">
                  No Guests Found
                </h3>
                <p className="text-sm sm:text-base text-gray-400">
                  No guests match your search criteria.
                </p>
              </div>
            ) : (
              <div className="grid gap-3 sm:gap-4 overflow-visible">
                {guests.map((guest) => (
                  <div
                    key={guest.id}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 relative overflow-visible"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                          <h3 className="text-base sm:text-lg font-bold text-white truncate">
                            {guest.guest_name}
                          </h3>
                          {guest.seat && (
                            <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-bold bg-blue-500 text-white border-2 border-blue-400 shadow-lg flex-shrink-0">
                              🪑 Seat: {guest.seat}
                            </span>
                          )}
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${getResponseStatusColor(
                              guest.response_status
                            )}`}
                          >
                            {guest.response_status.charAt(0).toUpperCase() +
                              guest.response_status.slice(1)}
                          </span>
                        </div>

                        {guest.companion_name && (
                          <p className="text-gray-300 text-xs sm:text-sm">
                            Companion: {guest.companion_name}
                            {guest.companion_count &&
                              guest.companion_count > 0 &&
                              ` (+${guest.companion_count})`}
                          </p>
                        )}

                        {guest.attendance_confirmed_at && (
                          <p className="text-gray-400 text-[10px] sm:text-xs mt-2">
                            Confirmed at:{" "}
                            {new Date(
                              guest.attendance_confirmed_at
                            ).toLocaleString()}
                          </p>
                        )}
                      </div>

                      <div className="flex-shrink-0 sm:ml-4">
                        <div className="flex items-center justify-start sm:justify-end">
                          <AttendanceDropdown
                            guestId={guest.id}
                            currentStatus={guest.attended}
                            onStatusChange={handleAttendanceToggle}
                            isUpdating={updating === String(guest.id)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination Controls */}
            {!searching && guests.length > 0 && totalPages > 1 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 mt-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-300">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} guests
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        currentPage === 1
                          ? 'opacity-50 cursor-not-allowed'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      First
                    </button>
                    
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        currentPage === 1
                          ? 'opacity-50 cursor-not-allowed'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                              currentPage === pageNum
                                ? 'bg-purple-600 text-white'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        currentPage === totalPages
                          ? 'opacity-50 cursor-not-allowed'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      Next
                    </button>
                    
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        currentPage === totalPages
                          ? 'opacity-50 cursor-not-allowed'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      Last
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Search Query State */}
        {!searchQuery.trim() && (
          <div className="text-center py-12 sm:py-16">
            <Search className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-lg sm:text-xl font-medium text-gray-300 mb-2">
              Search for Guests
            </h3>
            <p className="text-sm sm:text-base text-gray-400 max-w-md mx-auto px-4">
              Enter a guest's name in the search bar above to find and manage their attendance.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsherEventGuestsPage;
